<template>
  <v-container class="mt-4">
    <v-row v-resize="onResize">
      <v-col cols="12">
        <h1 class="mb-3" :class="{ 'text-h6': isMobile, 'text-h5': !isMobile}">請求情報</h1>
      </v-col>
      <v-col cols="12" v-if="errorMessage">
        <v-alert :value="true" type="error" v-html="$t(errorMessage)"></v-alert>
      </v-col>
      <v-col cols="6" md="4">
        <v-select
          v-model="selectedDeliveryMonth"
          :items="deliveryMonths"
          item-title="label"
          item-value="value"
          label="請求期間の対象を年月で絞り込む"
          class="select-digestives"
          @update:modelValue="previewDigestiveReport()"
          return-object
          variant="underlined"
        ></v-select>
      </v-col>
    </v-row>
    <v-row align="center" justify-center :class="{'mt-3': !isMobile}">
      <v-col cols="12">
        <span :class="{'text-h6': !isMobile, 'text-h7': isMobile}">合計情報</span>
        <span
          class="caption text-red-accent-4"
          style="cursor:pointer;"
          @click="doNoteScroll('1')"
        >※1</span>
      </v-col>
      <v-col cols="12">
        <v-data-table
          :headers="totalHeaders"
          :items="total"
          :hide-default-header="isMobile"
          no-data-text="表示対象がありません"
          hide-default-footer
          class="elevation-3"
          :class="{mobile: isMobile}"
        >
          <template v-slot:item="props">
            <tr v-if="!isMobile">
              <td class="text-right">{{ selectedDeliveryMonth.label }}</td>
              <td class="text-right">{{ number_format_yen(props.item.digestionAmount) }}</td>
              <td class="text-right">{{ number_format_yen(props.item.billingAmount) }}</td>
            </tr>
            <tr v-else>
              <td>
                <ul class="flex-content">
                  <li class="flex-item" data-label="請求月">{{ selectedDeliveryMonth.label }}</li>
                  <li
                    class="flex-item2"
                    data-label="消化金額"
                  >{{ number_format_yen(props.item.digestionAmount) }}</li>
                  <li
                    class="flex-item2"
                    data-label="請求金額"
                  >{{ number_format_yen(props.item.billingAmount) }}</li>
                </ul>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-row align="center" justify-center class="mt-3">
      <v-col cols="6">
        <div class="mb-2" :class="{'text-h6': !isMobile, 'text-h7': isMobile}">
          計測期間一覧
          <span
            class="caption text-red-accent-4"
            style="cursor:pointer;"
            @click="doNoteScroll('2')"
          >※2</span>
        </div>
      </v-col>
      <v-col cols="6" class="text-right">
        <v-btn color="light-green darken-1 text-white" @click="clickDownload()">
          <v-icon>mdi-download</v-icon>ダウンロード
        </v-btn>
      </v-col>
    </v-row>
    <v-row justify-center>
      <v-col cols="12">
        <v-data-table
          :headers="reportHeaders"
          :items="billingList"
          :hide-default-header="isMobile"
          no-data-text="表示対象がありません"
          hide-default-footer
          :sort-by="pagination.sortBy"
          :sortDesc="pagination.descending"
          :items-per-page="pagination.rowsPerPage"
          class="elevation-3"
          :class="{mobile: isMobile}"
        >
          <template v-slot:item="props">
            <tr v-if="!isMobile">
              <td
                class="text-center px-1"
              >{{ props.item.campaignPeriodStartAt }} - {{ props.item.campaignPeriodEndAt }}</td>
              <td class="text-center break-word-cell px-1">{{ props.item.advertiserName }}</td>
              <td class="text-center break-word-cell px-1">{{ props.item.campaignName }}</td>
              <td
                class="text-center px-1"
              >{{ props.item.cpxreportType }} / {{ number_format_yen(props.item.unitPrice) }}</td>
              <td class="text-right px-1">{{ number_format_yen(props.item.periodGoalBudget) }}</td>
              <td class="text-right px-1">{{ number_format(props.item.monthCount) }}</td>
              <!-- 累計消化金額 -->
              <td
                class="text-right px-1"
              >{{ number_format_yen(props.item.totalDigestionAmountAdjustment) }}</td>
              <!-- 当月消化金額 -->
              <td
                class="text-right px-1"
              >{{ number_format_yen(props.item.monthDigestionAmountAdjustment) }}</td>
              <!-- 請求金額 -->
              <td
                class="text-right px-1"
              >{{ number_format_yen(props.item.billingAmountAdjustment) }}</td>
            </tr>
            <tr v-else>
              <td>
                <ul class="flex-content">
                  <li
                    class="flex-item"
                    data-label="計測期間"
                  >{{ props.item.campaignPeriodStartAt }} - {{ props.item.campaignPeriodEndAt }}</li>
                  <li class="flex-item word-break" data-label="広告主">{{ props.item.advertiserName }}</li>
                  <li class="flex-item word-break" data-label="キャンペーン">{{ props.item.campaignName }}</li>
                  <li class="flex-item2" data-label="課金方式">{{ props.item.cpxreportType }}</li>
                  <li class="flex-item2" data-label="単価">{{ number_format_yen(props.item.unitPrice) }}</li>
                  <li
                    class="flex-item2"
                    data-label="想定ご予算"
                  >{{ number_format_yen(props.item.periodGoalBudget) }}</li>
                  <li
                    class="flex-item2"
                    data-label="当月実績"
                  >{{ number_format(props.item.monthCount) }}</li>
                  <li
                    class="flex-item2"
                    data-label="累計消化金額"
                  >{{ number_format_yen(props.item.totalDigestionAmountAdjustment) }}</li>
                  <li
                    data-label="当月消化金額"
                    class="flex-item2"
                  >{{ number_format_yen(props.item.monthDigestionAmountAdjustment) }}</li>
                  <li
                    data-label="請求金額"
                    class="flex-item2"
                  >{{ number_format_yen(props.item.billingAmountAdjustment) }}</li>
                </ul>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-col>
      <v-col cols="12">
        <p class="caption text-red-accent-4" id="note-1">
          ※1
          <br />端数を切り捨て処理しているため、合計金額にずれが生じる場合があります。
          <br />実際の請求金額は、調整金額による減算などにより異なる場合があります。
        </p>
        <p class="caption text-red-accent-4" id="note-2">
          ※2
          <br />累計消化金額、当月消化金額はグロス、請求金額はネットになります。
        </p>
      </v-col>
    </v-row>
    <v-dialog v-model="nodataDialog" scrollable max-width="300px">
      <v-card>
        <v-card-title>エラー</v-card-title>
        <v-divider></v-divider>
        <v-card-text height="300px">出力対象がありませんでした</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="nodataDialog = false">閉じる</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import CsvDownload from '../plugins/csvDownload'
import smoothscroll from 'smoothscroll-polyfill'
import { useFormatters } from '../plugins/format';

export default {
  name: 'Digestive',
  mixins: [CsvDownload],
  props: ['agencyId', 'advertiserId'],
  setup() {
    const {
      number_format,
      number_format_yen,
      percent_format,
    } = useFormatters();
    return {
      number_format,
      number_format_yen,
      percent_format,
    };
  },
  data: function () {
    return {
      // Data-Table ソート関連
      pagination: {
        // 2重クリック防止用
        sortBy: [{ key: '_sortPeriodValue', order:'asc' }],
        descending: false,
        rowsPerPage: -1
      },
      nodataDialog: false,
      deliveryMonths: [],
      selectedDeliveryMonth: {},
      totalHeaders: [
        {
          title: '請求月',
          align: 'center',
          sortable: false,
          key: 'monthlyDigestive'
        },
        {
          title: '消化金額',
          align: 'center',
          sortable: false,
          key: 'monthlyDigestiveBudget'
        },
        {
          title: '請求金額',
          align: 'center',
          sortable: false,
          key: 'digestedAmount'
        }
      ],
      total: [],
      reportHeaders: [
        {
          title: '計測期間',
          class: 'pa-1',
          align: 'center',
          key: '_sortPeriodValue',
          width: '110',
          sort: (a, b) => {
            const parseTimestamps = (item) => {
              const parts = item.split('-');
              const start = Number(parts[0]);
              const end = Number(parts[1]);

              if (isNaN(start) || isNaN(end)) {
                return { start: Infinity, end: Infinity };
              }
              return { start, end };
            };
            const periodA = parseTimestamps(a);
            const periodB = parseTimestamps(b);
            // 開始タイムスタンプで比較
            const compareStart = periodA.start - periodB.start;

            if (compareStart !== 0) {
              // 開始タイムスタンプが異なる場合、その差を返す
              return compareStart;
            } else {
              // 開始タイムスタンプが同じ場合、終了タイムスタンプで比較
              return periodA.end - periodB.end;
            }
          }
        },
        {
          title: '広告主',
          class: 'pa-1',
          align: 'center',
          key: 'advertiserName',
          width: '110'
        },
        {
          title: 'キャンペーン',
          class: 'pa-1 nowrap',
          align: 'center',
          key: 'campaignName'
        },
        {
          title: '課金方式 / 単価',
          class: 'pa-1',
          align: 'center',
          key: 'cpxType',
          width: '120',
          sortable: false
        },
        {
          title: '想定ご予算',
          class: 'pa-1 nowrap',
          align: 'center',
          key: `budget`,
          width: '95',
          sortable: false
        },
        {
          title: '当月実績',
          class: 'pa-1 nowrap',
          align: 'center',
          key: 'count',
          width: '85',
          sortable: false
        },
        {
          title: '累計消化金額',
          class: 'pa-1 nowrap',
          align: 'center',
          key: `totalDigestiveBudget`,
          width: '115',
          sortable: false
        },
        {
          title: '当月消化金額',
          class: 'pa-1 nowrap',
          align: 'center',
          key: `monthlyDigestiveBudget`,
          width: '115',
          sortable: false
        },
        {
          title: '請求金額',
          class: 'pa-1 nowrap',
          align: 'center',
          key: `digestiBility`,
          width: '90',
          sortable: false
        }
      ],
      billingList: [],
      isMobile: false,
      errorMessage: undefined
    }
  },
  methods: {
    ...mapActions('deliveryMonth', ['getMonthList']),
    ...mapActions('digestive', ['getReport']),
    initialize: function () {
      this.getMonthList().then((result) => {
        this.deliveryMonths = result
        // 前月を指定する。
        var defaultDeliveryMonth = result[2]
        if (this.$route.query.yearMonth !== undefined) {
          for (var i in result) {
            if (this.$route.query.yearMonth === result[i].value) {
              defaultDeliveryMonth = result[i]
            }
          }
        }
        this.selectedDeliveryMonth = defaultDeliveryMonth
        this.previewDigestiveReport()
      })
    },
    previewDigestiveReport: function () {
      const jwtToken = this.$store.getters['auth/jwtToken']
      const deliveryMonth = this.selectedDeliveryMonth.value
      const agencyId = this.agencyId
      const advertiserId = this.advertiserId

      // URLにパラメータを付与
      this.$router.replace({
        query: { yearMonth: deliveryMonth }
      }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error('Router replace error:', err);
        }
      });

      this.getReport({
        privateCode: process.env.ProjectCode,
        agencyId: agencyId,
        advertiserId: advertiserId,
        yyyymm: deliveryMonth,
        jwtToken: jwtToken
      })
        .then((result) => {
          this.errorMessage = undefined
          if (!result) {
            // 表示するデータがない場合
            this.total = []
            this.billingList = []
            return
          }
          this.total = [result.total]
          this.billingList = result.billingList
            .filter(item => item !== undefined && item !== null)
            .map(item => {
              // ソートキーの生成
              const startMoment = moment(item.campaignPeriodStartAt, 'YYYY/MM/DD')
              const endMoment = moment(item.campaignPeriodEndAt, 'YYYY/MM/DD')

              return {
                ...item,
                _sortPeriodValue: startMoment + '-' + endMoment
              };
            })
        })
        .catch((err) => {
          if (err instanceof ReferenceError) {
            this.errorMessage = err.message
          } else {
            // idToken が切れているのでセッションを切り、ログイン画面に遷移する
            this.$store.dispatch('auth/logout')
            this.$router.go({ path: this.$router.currentRoute.path, force: true })
          }
        })
    },
    clickDownload: function () {
      this.isDownloadCsv = true
      const billingList = this.$store.getters['digestive/billingList']
      if (!billingList || billingList.length <= 0) {
        this.nodataDialog = true
        this.isDownloadCsv = false
        return
      }
      let csvList = []
      csvList.push(
        '計測期間,広告主,キャンペーン,課金方式,単価,請求年月,想定ご予算,当月実績,累計消化金額 (グロス),当月消化金額 (グロス),請求金額 (ネット)'
      )
      billingList.forEach((el) => {
        var line =
          `${el['campaignPeriodStartAt']} - ${el['campaignPeriodEndAt']},${el['advertiserName']},${el['campaignName']}` +
          `,${el['cpxreportType']},${el['unitPrice']},${el['billingTargetMonth']},${el['periodGoalBudget']},${el['monthCount']}` +
          `,${el['totalDigestionAmountAdjustment']}` +
          `,${el['monthDigestionAmountAdjustment']},${el['billingAmountAdjustment']}`
        csvList.push(line)
      })
      this.csvDownload(csvList, 'budget', [this.selectedDeliveryMonth.value, this.agencyId])
      this.isDownloadCsv = false
    },
    onResize: function () {
      const elements = document.getElementsByClassName('nowrap')
      Array.from(elements).forEach(function (el) {
        el.setAttribute('style', 'white-space:nowrap;')
      })
      if (window.innerWidth < 960) {
        this.isMobile = true
      } else {
        this.isMobile = false
      }
    },
    doNoteScroll: function (no) {
      smoothscroll.polyfill()
      document.querySelector(`#note-${no}`).scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  },
  beforeRouteUpdate: function (to, from, next) {
    next()
  },
  mounted: function () {
    this.initialize()
  }
}
</script>
